import { endOfDay, endOfToday, startOfDay, startOfToday } from "date-fns";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import ActivityStatus from "@mapmycustomers/shared/enum/activity/ActivityStatus";
import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import {
  Activity,
  EntityTypesSupportedByMapsPage,
  MapEntity,
} from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Company from "@mapmycustomers/shared/types/entity/Company";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { StageSummary } from "@mapmycustomers/shared/types/entity/deals/StageSummary";
import { MapEntry } from "@mapmycustomers/shared/types/map";
import Organization from "@mapmycustomers/shared/types/Organization";
import { OrganizationSize } from "@mapmycustomers/shared/types/OrganizationSize";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel, {
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import { ActivityCount } from "@app/scene/home/component/Cards/util";
import type { DealsRottingFilter } from "@app/scene/home/types/DealsRottingFilter";
import type RecordsOutOfCadenceFilter from "@app/scene/home/types/RecordsOutOfCadenceFilter";
import type RecordsSourceFilter from "@app/scene/home/types/RecordsSourceFilter";
import getGranularity from "@app/scene/home/util/getGranularity";
import homeSettings from "@app/scene/home/util/homeSettings";
import { GroupByType } from "@app/scene/reports/components/Overview/components/Engagement/enum/GroupByType";
import { getActivityTypes } from "@app/store/activity";
import { callApi, callReadonlyApi } from "@app/store/api/callApi";
import { getFunnelStages } from "@app/store/deal";
import { handleError } from "@app/store/errors/actions";
import {
  getCurrentUser,
  getOrganization,
  getOrganizationId,
  getOrganizationSize,
  isCurrentUserMember,
} from "@app/store/iam";
import SourceSummary from "@app/types/source/SourceSummary";
import Statistics from "@app/types/Statistics";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import ListResponseAggregation from "@app/types/viewModel/ListResponseAggregation";
import getActivityTypeWithCount from "@app/util/activity/getActivityTypeWithCount";
import { PREVIEW_MODAL_LIMIT } from "@app/util/consts";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import isValidDate from "@app/util/isValidDate";
import getPrecisionDetails from "@app/util/map/getPrecisionDetails";
import { parseApiDate } from "@app/util/parsers";

import { DealsGroupByType } from "../enum/DealsGroupByType";
import { DealsGroupBy } from "../types/DealGroupBy";
import { SupportingGroupsRecordStats } from "../types/RecordsOutOfCadence";
import { RecordsStats } from "../types/RecordsStats";
import { getActivityRangeStartDate } from "../util/activityDateRange";
import getStartDate from "../util/getStartDate";

import {
  fetchActivityByRepData,
  fetchActivityByTypesData,
  fetchActivityOverview,
  fetchDealsRotting,
  fetchEngagementRecordsList,
  fetchFunnelSummary,
  fetchOpenDealsData,
  fetchRecentCompanies,
  fetchRecordsBySourceForSelectedType,
  fetchRecordsBySourceSummary,
  fetchRecordsOutOfCadence,
  fetchSelectedActivityByTypesData,
  fetchTodayActivitiesData,
  fetchTodayActivitiesDataForReps,
  initialize,
  setDealsRottingFilter,
  setRecordSourceFilter,
  setRecordsOutOfCadenceFilter,
} from "./actions";
import { initialState } from "./HomeState";
import { getRecentCompaniesCriteria, getRecordsOutOfCadenceFilter } from "./selectors";

type ActivitiesByRepSecondaryItem = { doc_count: number; key_as_string: string };

export function* onInitialize() {
  try {
    const member: boolean = yield select(isCurrentUserMember);
    const organization: Organization = yield select(getOrganization);
    const [anyActivitiesResponse, dealsCountResponse, accountsCountResponse]: [
      ListResponse<Activity>,
      ListResponse<Deal>,
      ListResponse<Company>,
    ] = yield all([
      callApi("fetchActivities", organization.id, {
        $columns: ["id"],
        $limit: 0,
      }),
      callApi("fetchDeals", organization.id, {
        $columns: ["id"],
        $limit: 0,
      }),
      callApi("fetchCompanies", organization.id, {
        $columns: ["id"],
        $limit: 0,
      }),
    ]);

    const dealsRottingFilter: DealsRottingFilter =
      (yield call([homeSettings, homeSettings.getDealsRottingCardState])) ??
      initialState.dealsRottingFilter;
    if (
      member &&
      [DealsGroupByType.TEAM, DealsGroupByType.USER].includes(dealsRottingFilter.groupByType)
    ) {
      dealsRottingFilter.groupByType = DealsGroupByType.FUNNEL;
    }

    const recordsOutOfCadenceFilter: RecordsOutOfCadenceFilter =
      (yield call([homeSettings, homeSettings.getEngagementCardState])) ??
      initialState.recordsOutOfCadenceFilter;
    const recordsSourceFilter: RecordsSourceFilter =
      (yield call([homeSettings, homeSettings.getRecordsSourceCardState])) ??
      initialState.recordsSourceFilter;

    yield put(
      initialize.success({
        dealsCount: dealsCountResponse.total,
        dealsRottingFilter,
        hasActivities: anyActivitiesResponse.count > 0,
        hasCompanies: accountsCountResponse.count > 0,
        recordsOutOfCadenceFilter,
        recordsSourceFilter,
      })
    );
  } catch (error) {
    yield put(initialize.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchTodayActivitiesData({
  payload,
}: ReturnType<typeof fetchTodayActivitiesData.request>) {
  try {
    const { activityTypes, assigneeIds, date, status } = payload;
    const organization: Organization = yield select(getOrganization);
    let completed;
    let order = "-updatedAt";
    if (status === ActivityStatusOption.COMPLETED) {
      completed = true;
      order = "-completedAt";
    } else if (status === ActivityStatusOption.INCOMPLETE) {
      completed = false;
      order = "-startAt";
    }

    const response: ListResponse<Activity> = yield callApi("fetchActivities", organization.id, {
      $columns: [
        "crmActivityType",
        "account",
        "completed",
        "completedAt",
        "contact",
        "deal",
        "id",
        "name",
        "startAt",
        "user",
      ],
      $filters: {
        $and: [
          {
            assigneeId: { $in: assigneeIds },
            "crmActivityType.id": { $in: activityTypes.map(({ id }) => id) },
            startAt: {
              $gte: startOfDay(new Date(date)).toISOString(),
              $lte: endOfDay(new Date(date)).toISOString(),
            },
          },
        ],
        completed,
        includeAccessStatus: true,
      },
      $order: order,
    });

    yield put(fetchTodayActivitiesData.success(response.data));
  } catch (error) {
    yield put(fetchTodayActivitiesData.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchTodayActivitiesDataForReps({
  payload,
}: ReturnType<typeof fetchTodayActivitiesDataForReps.request>) {
  try {
    const { dateRange, status } = payload;
    const organization: Organization = yield select(getOrganization);
    const me: User = yield select(getCurrentUser);
    const completed = status === ActivityStatus.COMPLETED;
    let dateFilter: PlatformFilterCondition = {
      startAt: {
        $gte: startOfToday().toISOString(),
        $lte: getActivityRangeStartDate(dateRange)?.toISOString(),
      },
    };
    if (status === ActivityStatus.COMPLETED) {
      dateFilter = {
        completedAt: {
          $gte: getActivityRangeStartDate(dateRange)?.toISOString(),
          $lte: endOfToday().toISOString(),
        },
      };
    }
    if (status === ActivityStatus.OVERDUE) {
      dateFilter = {
        startAt: {
          $gte: getActivityRangeStartDate(dateRange)?.toISOString(),
          $lte: new Date().toISOString(),
        },
      };
    }

    const response: ListResponse<Activity> = yield callApi("fetchActivities", organization.id, {
      $columns: [
        "crmActivityType",
        "id",
        "name",
        "note",
        "assignee",
        "user",
        "startAt",
        "completed",
        "completedAt",
        "account",
        "contact",
        "deal",
      ],
      $filters: {
        $and: [dateFilter],
        completed,
        includeAccessStatus: true,
        viewAs: me.id,
      },
      $limit: 999,
      $order: completed ? "-completedAt" : "startAt",
    });

    yield put(fetchTodayActivitiesDataForReps.success(response.data));
  } catch (error) {
    yield put(fetchTodayActivitiesDataForReps.failure(error));
  }
}

export function* onFetchActivityByRepData({
  payload,
}: ReturnType<typeof fetchActivityByRepData.request>) {
  try {
    const { activityTypeIds, assigneeIds, dateRange } = payload;
    const organization: Organization = yield select(getOrganization);
    const granularity = getGranularity(dateRange);

    const response: AggregatedListResponse<
      Activity,
      { key: User["id"]; secondary: ActivitiesByRepSecondaryItem[] }[]
    > = yield callApi("fetchActivities", organization.id, {
      $aggs: {
        primaryGroup: { field: "assignee.id" },
        secondaryGroup: {
          field: "startAt",
          interval: granularity ?? "day",
          offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
          timezone: getLocalTimeZone(),
        },
      },
      $filters: {
        $and: [
          {
            assigneeId: { $in: assigneeIds },
            "crmActivityType.id": { $in: activityTypeIds },
            startAt: {
              $gte: dateRange.startDate.toISOString(),
              $lte: dateRange.endDate.toISOString(),
            },
          },
        ],
        includeAccessStatus: true,
      },
      $limit: 0,
    });

    const userIdToActivityCountMap: Record<User["id"], ActivityCount[]> =
      response.aggregations.reduce((result, primaryItem) => {
        const dates = primaryItem.secondary
          .map<ActivityCount>((secondaryItem: { doc_count: number; key_as_string: string }) => ({
            count: secondaryItem.doc_count,
            date: parseApiDate(secondaryItem.key_as_string),
          }))
          .filter(({ date }) => isValidDate(date));

        return Object.assign(result, { [primaryItem.key]: dates });
      }, {});

    yield put(fetchActivityByRepData.success(userIdToActivityCountMap));
  } catch (error) {
    yield put(fetchActivityByRepData.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchActivityByTypesData({
  payload,
}: ReturnType<typeof fetchActivityByTypesData.request>) {
  try {
    const { assigneeIds, crmActivityTypeIds, dateRange } = payload;
    const organization: Organization = yield select(getOrganization);
    const response: AggregatedListResponse<Activity, ListResponseAggregation<Activity>[]> =
      yield callApi("fetchActivities", organization.id, {
        $aggs: { primaryGroup: { field: "crmActivityTypeId" } },
        $filters: {
          $and: [
            {
              assigneeId: { $in: assigneeIds },
              "crmActivityType.id": { $in: crmActivityTypeIds },
              startAt: {
                $gte: dateRange.startDate.toISOString(),
                $lte: dateRange.endDate.toISOString(),
              },
            },
          ],
          completed: true,
        },
        $limit: 0,
      });
    const activityTypes: ActivityType[] = yield select(getActivityTypes);
    const activityTypesWithCount = getActivityTypeWithCount(activityTypes, response.aggregations);
    yield put(fetchActivityByTypesData.success(activityTypesWithCount));
  } catch (error) {
    yield put(fetchActivityByTypesData.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchSelectedActivityByTypesData({
  payload,
}: ReturnType<typeof fetchSelectedActivityByTypesData.request>) {
  try {
    const { assigneeIds, crmActivityTypeIds, dateRange } = payload;
    const organization: Organization = yield select(getOrganization);
    const response: ListResponse<Activity> = yield callApi("fetchActivities", organization.id, {
      $filters: {
        $and: [
          {
            assigneeId: { $in: assigneeIds },
            "crmActivityType.id": crmActivityTypeIds,
            startAt: {
              $gte: dateRange.startDate.toISOString(),
              $lte: dateRange.endDate.toISOString(),
            },
          },
        ],
        completed: true,
      },
      $limit: PREVIEW_MODAL_LIMIT,
      $offset: ((payload.page ?? 1) - 1) * PREVIEW_MODAL_LIMIT,
      $order: "-startAt",
    });
    yield put(
      fetchSelectedActivityByTypesData.success({
        activities: response.data,
        totalRecords: response.total,
      })
    );
  } catch (error) {
    yield put(fetchSelectedActivityByTypesData.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchRecordsBySourceSummary({
  payload: { dateRange, entityTypes, userId },
}: ReturnType<typeof fetchRecordsBySourceSummary.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: AggregatedListResponse<MapEntity, SourceSummary[]> = yield callApi(
      "fetchPins",
      orgId,
      {
        $aggs: {
          primaryGroup: { field: "sourceCreated.source" },
        },
        $filters: {
          $and: [
            {
              createdAt: {
                $gte: getStartDate(dateRange)?.toISOString(),
                $lte: endOfToday().toISOString(),
              },
              userId: { $in: userId },
            },
          ],
          entities: Object.fromEntries(entityTypes.map((type) => [type, {}])),
        },
        $limit: 0,
      }
    );
    yield put(fetchRecordsBySourceSummary.success(response.aggregations));
  } catch (error) {
    yield put(fetchRecordsBySourceSummary.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchOpenDealsData({ payload }: ReturnType<typeof fetchOpenDealsData.request>) {
  try {
    const { funnelId, usersIds } = payload;
    const organization: Organization = yield select(getOrganization);

    const funnelStages: Record<Funnel["id"], Stage[]> = yield select(getFunnelStages);
    const stages: Stage[] = funnelStages[funnelId] ?? [];

    // platform only supports filtering by stage name, not by stage type
    // so we fetch stages first, find names of closed ones and use them in a deals filter below
    const closedStagesNames = stages
      .filter(({ type }) => [DealStageType.LOST, DealStageType.WON].includes(type))
      .map(({ name }) => name);

    const dealsResponse: ListResponse<Deal> = yield callApi("fetchDeals", organization.id, {
      $filters: {
        $and: [
          {
            funnelId,
            // only fetch open deals:
            stage: { $nin: closedStagesNames },
          },
        ],
        includeAccessStatus: true,
        mapped: true, // only fetch mappable deals
        userId: { $in: usersIds },
      },
      $limit: 1000,
      $order: "-createdAt",
    });

    yield put(
      fetchOpenDealsData.success({
        deals: dealsResponse.data,
      })
    );
  } catch {
    yield put(fetchOpenDealsData.failure());
  }
}

export function* onFetchActivityOverview({
  payload,
}: ReturnType<typeof fetchActivityOverview.request>) {
  try {
    const { dateRange, usersIds } = payload;
    const organization: Organization = yield select(getOrganization);
    const response: ListResponse<Statistics> = yield callApi("fetchStatistics", organization.id, {
      date: { $gte: getStartDate(dateRange)?.toISOString(), $lte: endOfToday().toISOString() },
      summary: "all",
      userId: usersIds && usersIds.length ? { $in: usersIds } : undefined,
    });

    yield put(fetchActivityOverview.success(response.data.length ? response.data[0].values : {}));
  } catch (error) {
    yield put(fetchActivityOverview.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchFunnelSummary({ payload }: ReturnType<typeof fetchFunnelSummary.request>) {
  try {
    const { selectedUserIds } = payload;
    const organization: Organization = yield select(getOrganization);
    const response: ListResponse<StageSummary> = yield callApi(
      "fetchAllFunnelSummary",
      organization.id,
      payload.funnelId,
      {
        $filters: {
          userId: { $in: selectedUserIds },
        },
      }
    );

    yield put(fetchFunnelSummary.success(response.data.filter(({ totalValue }) => totalValue > 0)));
  } catch (error) {
    yield put(fetchFunnelSummary.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchRecentCompanies() {
  try {
    const { bounds, limit, usersIds, zoom } = yield select(getRecentCompaniesCriteria);
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const organizationSize: OrganizationSize = yield select(getOrganizationSize);

    const pinsResponse: ListResponse<MapEntry> = yield callReadonlyApi("fetchMapPins", orgId, {
      $filters: {
        bounds,
        entities: { accounts: { includeAccessStatus: true } },
        mapped: true,
        userId: usersIds && usersIds.length ? { $in: usersIds } : undefined,
        ...getPrecisionDetails(zoom ?? 1, organizationSize, 4),
        precisionThreshold: 100,
      },
      $limit: limit,
      $order: "-updatedAt",
    });

    yield put(fetchRecentCompanies.success(pinsResponse.data));
  } catch (error) {
    yield put(fetchRecentCompanies.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchRecordsOutOfCadence({
  payload: { groupIds, teamIds, userIds },
}: ReturnType<typeof fetchRecordsOutOfCadence.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const { groupByType, recordTypes }: RecordsOutOfCadenceFilter = yield select(
      getRecordsOutOfCadenceFilter
    );

    const [primaryGroupField, ids] =
      groupByType === GroupByType.GROUP
        ? ["groups", groupIds]
        : groupByType === GroupByType.TEAM
          ? ["teams", teamIds]
          : ["userId", userIds];

    const aggregationPayload = {
      $aggs: {
        count: ["id"],
        primaryGroup: { field: primaryGroupField },
      },
      $filters: {
        ...(groupByType === GroupByType.USER && ids ? { userId: { $in: ids } } : {}),
        ...(groupByType === GroupByType.TEAM && ids ? { teamId: { $in: ids } } : {}),
        entities: recordTypes.reduce((result, entityType) => {
          return {
            ...result,
            [entityType]: {
              includeGroups: true,
              ...(groupByType === GroupByType.GROUP && ids ? { groups: { $any: ids } } : {}),
            },
          };
        }, {}),
      },
      $limit: 0,
    };

    const aggregationPayloadWithCadence = {
      ...aggregationPayload,
      $filters: {
        ...aggregationPayload.$filters,
        $and: [{ cadenceDaysOut: { $gt: 0 } }],
      },
    };

    const [response, responseWithCadence]: [
      AggregatedListResponse<any, ListResponseAggregation<any>[]>,
      AggregatedListResponse<any, ListResponseAggregation<any>[]>,
    ] = yield all([
      callApi("fetchPins", organization.id, aggregationPayload),
      callApi("fetchPins", organization.id, aggregationPayloadWithCadence),
    ]);
    const result: SupportingGroupsRecordStats = {};
    const idSet = new Set(ids ?? []);
    responseWithCadence.aggregations.forEach((row: ListResponseAggregation<any>) => {
      if (idSet.has(parseInt(row.key, 10))) {
        result[row.key] = { relevant: row.doc_count ?? 0, total: 0 };
      }
    });
    response.aggregations.forEach((row: ListResponseAggregation<any>) => {
      if (result[row.key]) {
        result[row.key] = { ...result[row.key], total: row.doc_count ?? 0 };
      }
    });
    yield put(fetchRecordsOutOfCadence.success(result));
  } catch (error) {
    yield put(fetchRecordsOutOfCadence.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchDealsRotting({ payload }: ReturnType<typeof fetchDealsRotting.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    let $aggs;
    if (payload.dealsGroupByType === DealsGroupByType.FUNNEL) {
      $aggs = {
        innerHits: { columns: ["funnel.id", "stage.id"], limit: 1 },
        primaryGroup: { field: "funnel.name" },
        secondaryGroup: { field: "stage.name" },
      };
    } else if (payload.dealsGroupByType === DealsGroupByType.USER) {
      $aggs = {
        innerHits: { columns: ["user.username"], limit: 1 },
        primaryGroup: { field: "user.id" },
      };
    } else {
      $aggs = {
        primaryGroup: { field: "teams" },
      };
    }
    const dealsAggregationResponse: AggregatedListResponse<Deal> = yield callApi(
      "fetchDeals",
      organization.id,
      {
        $aggs,
        $limit: 0,
      }
    );
    const dealsAggregationMap: Record<string, number> =
      dealsAggregationResponse.aggregations.reduce(
        (result: Record<string, number>, record: ListResponseAggregation<Deal>) => {
          return {
            ...result,
            [record.key]: record.doc_count,
          };
        },
        {}
      );
    const rottingDealsAggregationResponse: AggregatedListResponse<Deal> = yield callApi(
      "fetchDeals",
      organization.id,
      {
        $aggs,
        $filters: {
          $and: [{ rottingDaysOut: { $gt: 0 } }],
        },
        $limit: 0,
      }
    );

    const dealsRottingStats: Record<DealsGroupBy["id"], RecordsStats> =
      rottingDealsAggregationResponse.aggregations.reduce(
        (
          result: Record<DealsGroupBy["id"], RecordsStats>,
          record: ListResponseAggregation<Deal>
        ) => {
          let id;
          if (payload.dealsGroupByType === DealsGroupByType.FUNNEL) {
            id = record.secondary[0].data[0].funnel.id;
          } else {
            id = record.key;
          }
          return {
            ...result,
            [id]: {
              relevant: record.doc_count,
              total: dealsAggregationMap[record.key],
            },
          };
        },
        {}
      );

    let rottingStagesStats: Record<DealsGroupBy["id"], Record<Stage["name"], RecordsStats>> = {};
    if (payload.dealsGroupByType === DealsGroupByType.FUNNEL) {
      const stagesStats: Record<
        string,
        Record<Stage["name"], number>
      > = dealsAggregationResponse.aggregations.reduce(
        (
          result: Record<string, Record<Stage["name"], number>>,
          record: ListResponseAggregation<Deal>
        ) => {
          const stagesMap: Record<Stage["name"], number> = record.secondary.reduce(
            (result: Record<Stage["name"], number>, record: ListResponseAggregation<Deal>) => {
              return {
                [record.key]: record.doc_count,
                ...result,
              };
            },
            {}
          );
          return {
            ...result,
            [record.key]: stagesMap,
          };
        },
        {}
      );

      rottingStagesStats = rottingDealsAggregationResponse.aggregations.reduce(
        (
          result: Record<DealsGroupBy["id"], Record<Stage["name"], RecordsStats>>,
          funnel: ListResponseAggregation<Deal>
        ) => {
          const stagesMap: Record<Stage["name"], RecordsStats> = funnel.secondary.reduce(
            (result: Record<Stage["name"], RecordsStats>, stage: ListResponseAggregation<Deal>) => {
              return {
                [stage.key]: {
                  relevant: stage.doc_count,
                  total: stagesStats[funnel.key][stage.key],
                },
                ...result,
              };
            },
            {}
          );
          return {
            ...result,
            [funnel.secondary[0].data[0].funnel.id]: stagesMap,
          };
        },
        {}
      );
    }

    yield put(
      fetchDealsRotting.success({
        dealsRottingStats,
        stagesStats: rottingStagesStats,
      })
    );
  } catch (error) {
    yield put(fetchDealsRotting.failure(error));
    yield put(handleError({ error }));
  }
}

function* onFilterUpdate({
  payload: updatedFilter,
}: ReturnType<typeof setRecordsOutOfCadenceFilter>) {
  yield call([homeSettings, homeSettings.setEngagementCardState], updatedFilter);
}

function* onRecordsSourceFilterUpdate({
  payload: updatedFilter,
}: ReturnType<typeof setRecordSourceFilter>) {
  yield call([homeSettings, homeSettings.setRecordsSourceCardState], updatedFilter);
}

function* onDealsRottingFilterUpdate({
  payload: updatedFilter,
}: ReturnType<typeof setDealsRottingFilter>) {
  yield call([homeSettings, homeSettings.setDealsRottingCardState], updatedFilter);
}

export function* onFetchEngagementRecordsList({
  payload,
}: ReturnType<typeof fetchEngagementRecordsList.request>) {
  try {
    const entitiesFilters: Partial<Record<EntityTypesSupportedByMapsPage, PlatformFilterModel>> =
      {};
    // each entity gets a group filter, whereas it is only valid for one of them (whose type matches group type)
    // this is ok to proceed with this per Platform team, because Platform will simply ignore invalid filters

    payload.recordTypes.forEach((entityType) => {
      if (payload.type === GroupByType.GROUP) {
        entitiesFilters[entityType] = { groups: { $any: [payload.id] }, includeGroups: true };
      } else {
        entitiesFilters[entityType] = { includeGroups: true };
      }
    });

    const organization: Organization = yield select(getOrganization);
    let idFilter: {};
    if (payload.type === GroupByType.USER) {
      idFilter = {
        cadenceDaysOut: { $gt: 0 },
        entities: entitiesFilters,
        userId: { $in: payload.id },
      };
    } else if (payload.type === GroupByType.TEAM) {
      idFilter = {
        cadenceDaysOut: { $gt: 0 },
        entities: entitiesFilters,
        teamId: { $in: payload.id },
      };
    } else {
      idFilter = { cadenceDaysOut: { $gt: 0 }, entities: entitiesFilters };
    }

    const params = {
      $filters: idFilter,
      $limit: PREVIEW_MODAL_LIMIT,
      $offset: ((payload.page ?? 1) - 1) * PREVIEW_MODAL_LIMIT,
    };
    const recordsResponse: ListResponse<MapEntity> = yield callApi(
      "fetchPins",
      organization.id,
      params
    );
    yield put(
      fetchEngagementRecordsList.success({
        records: recordsResponse.data,
        totalRecords: recordsResponse.total,
      })
    );
  } catch (error) {
    yield put(fetchEngagementRecordsList.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchRecordsBySourceForSelectedType({
  payload,
}: ReturnType<typeof fetchRecordsBySourceForSelectedType.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const filter = {
      $and: [
        {
          createdAt: {
            $gte: getStartDate(payload.dateRange)?.toISOString(),
            $lte: endOfToday().toISOString(),
          },
          userId: { $in: payload.userId },
        },
        { "sourceCreated.source": payload.sourceType },
      ],
      entities: Object.fromEntries(payload.entityTypes.map((type) => [type, {}])),
    };

    const params = {
      $filters: filter,
      $limit: PREVIEW_MODAL_LIMIT,
      $offset: ((payload.page ?? 1) - 1) * PREVIEW_MODAL_LIMIT,
    };
    const recordsResponse: ListResponse<MapEntity> = yield callApi(
      "fetchPins",
      organization.id,
      params
    );
    yield put(
      fetchRecordsBySourceForSelectedType.success({
        recordsBySourceList: recordsResponse.data,
        totalRecords: recordsResponse.total,
      })
    );
  } catch (error) {
    yield put(fetchRecordsBySourceForSelectedType.failure(error));
    yield put(handleError({ error }));
  }
}

export function* homeSaga() {
  yield takeLatest(initialize.request, onInitialize);
  yield takeLatest(fetchRecentCompanies.request, onFetchRecentCompanies);
  yield takeLatest(fetchTodayActivitiesData.request, onFetchTodayActivitiesData);
  yield takeLatest(fetchTodayActivitiesDataForReps.request, onFetchTodayActivitiesDataForReps);
  yield takeLatest(fetchActivityByRepData.request, onFetchActivityByRepData);
  yield takeLatest(fetchActivityByTypesData.request, onFetchActivityByTypesData);
  yield takeLatest(fetchSelectedActivityByTypesData.request, onFetchSelectedActivityByTypesData);
  yield takeLatest(fetchRecordsBySourceSummary.request, onFetchRecordsBySourceSummary);
  yield takeLatest(fetchActivityOverview.request, onFetchActivityOverview);
  yield takeLatest(fetchFunnelSummary.request, onFetchFunnelSummary);
  yield takeLatest(fetchOpenDealsData.request, onFetchOpenDealsData);
  yield takeLatest(fetchRecordsOutOfCadence.request, onFetchRecordsOutOfCadence);
  yield takeLatest(setRecordSourceFilter, onRecordsSourceFilterUpdate);
  yield takeLatest(setRecordsOutOfCadenceFilter, onFilterUpdate);
  yield takeLatest(fetchDealsRotting.request, onFetchDealsRotting);
  yield takeLatest(setDealsRottingFilter, onDealsRottingFilterUpdate);
  yield takeLatest(fetchEngagementRecordsList.request, onFetchEngagementRecordsList);
  yield takeLatest(
    fetchRecordsBySourceForSelectedType.request,
    onFetchRecordsBySourceForSelectedType
  );
}
